import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Ticket, Plus, RefreshCcw, Send, MessageSquare, Filter,
  AlertCircle, Clock, CheckCircle, XCircle, User, Building2,
  Paperclip, ChevronRight, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

// Status badges
const STATUS_CONFIG = {
  open: { label: 'Ouvert', bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
  in_progress: { label: 'En cours', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
  resolved: { label: 'Résolu', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
  closed: { label: 'Fermé', bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Basse', bg: 'bg-slate-100', text: 'text-slate-600' },
  normal: { label: 'Normale', bg: 'bg-blue-100', text: 'text-blue-600' },
  urgent: { label: 'Urgente', bg: 'bg-red-100', text: 'text-red-600' },
};

const CATEGORY_LABELS = {
  course_issue: 'Problème cours',
  homework_evaluation: 'Devoir / Évaluation',
  planning_issue: 'Planning',
  access_issue: 'Accès',
  administrative: 'Administratif',
  student_issue: 'Problème étudiant',
  course_management: 'Gestion cours',
  technical: 'Technique',
  platform_bug: 'Bug plateforme',
  feature_request: 'Demande fonctionnalité',
  billing: 'Facturation',
  other: 'Autre',
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const CategoryBadge = ({ category }) => (
  <span className="px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
    {CATEGORY_LABELS[category] || category}
  </span>
);

const TicketSupportPage = ({ onMenuClick }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [recipients, setRecipients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [ticketType, setTicketType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  // Determine user role
  const userRole = user?.role || 'student';

  // Fetch data
  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (ticketType && ticketType !== 'all') params.append('ticket_type', ticketType);
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);

      const response = await api.get(`/support/tickets?${params.toString()}`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Erreur lors du chargement des tickets');
    }
  }, [ticketType, statusFilter, priorityFilter]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/support/tickets/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await api.get('/support/recipients');
      setRecipients(response.data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/support/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchStats(), fetchRecipients(), fetchCategories()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTickets]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchTickets(), fetchStats()]);
    setLoading(false);
    toast.success('Tickets actualisés');
  };

  // Section title based on role
  const getSectionTitle = () => {
    switch (userRole) {
      case 'student': return 'Mes Tickets';
      case 'trainer': return 'Support';
      case 'cfa_admin': return 'Support du Centre';
      case 'super_admin': return 'Support Plateforme';
      default: return 'Support';
    }
  };

  // Filter options based on role
  const getTicketTypeOptions = () => {
    if (userRole === 'trainer') {
      return [
        { value: 'all', label: 'Tous les tickets' },
        { value: 'received', label: 'Reçus des étudiants' },
        { value: 'sent', label: 'Envoyés au CFA Admin' },
      ];
    }
    if (userRole === 'cfa_admin') {
      return [
        { value: 'all', label: 'Tous les tickets' },
        { value: 'received_students', label: 'Reçus des étudiants' },
        { value: 'received_trainers', label: 'Reçus des formateurs' },
        { value: 'sent', label: 'Envoyés au Super Admin' },
      ];
    }
    return null;
  };

  if (loading && !tickets.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title={getSectionTitle()} onMenuClick={onMenuClick} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats.open || 0}</p>
                  <p className="text-sm text-blue-600">Ouverts</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-amber-700">{stats.in_progress || 0}</p>
                  <p className="text-sm text-amber-600">En cours</p>
                </div>
                <Clock className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{stats.resolved || 0}</p>
                  <p className="text-sm text-emerald-600">Résolus</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-slate-700">{stats.closed || 0}</p>
                  <p className="text-sm text-slate-600">Fermés</p>
                </div>
                <XCircle className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-ticket-btn">
              <Plus className="w-4 h-4 mr-2" />
              Créer un ticket
            </Button>
            <Button variant="outline" onClick={handleRefresh} data-testid="refresh-tickets-btn">
              <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            {getTicketTypeOptions() && (
              <select
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                data-testid="ticket-type-filter"
              >
                {getTicketTypeOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
              data-testid="status-filter"
            >
              <option value="">Tous statuts</option>
              <option value="open">Ouverts</option>
              <option value="in_progress">En cours</option>
              <option value="resolved">Résolus</option>
              <option value="closed">Fermés</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
              data-testid="priority-filter"
            >
              <option value="">Toutes priorités</option>
              <option value="urgent">Urgentes</option>
              <option value="normal">Normales</option>
              <option value="low">Basses</option>
            </select>
          </div>
        </div>

        {/* Tickets List or Detail View */}
        {selectedTicket ? (
          <TicketDetail
            ticketId={selectedTicket}
            onBack={() => {
              setSelectedTicket(null);
              fetchTickets();
              fetchStats();
            }}
            userRole={userRole}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-violet-600" />
                {tickets.length} Ticket{tickets.length !== 1 ? 's' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tickets.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun ticket trouvé</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Créer votre premier ticket
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {tickets.map(ticket => (
                    <div
                      key={ticket.id}
                      className="py-4 hover:bg-slate-50 cursor-pointer transition-colors rounded-lg px-2 -mx-2"
                      onClick={() => setSelectedTicket(ticket.id)}
                      data-testid={`ticket-item-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-slate-900">{ticket.subject}</h3>
                            <StatusBadge status={ticket.status} />
                            <PriorityBadge priority={ticket.priority} />
                          </div>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-1">
                            {ticket.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.is_creator ? 'Vous' : ticket.creator?.name}
                            </span>
                            {ticket.recipient && (
                              <span>
                                → {ticket.is_recipient ? 'Vous' : ticket.recipient.name}
                              </span>
                            )}
                            {ticket.organization && userRole === 'super_admin' && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {ticket.organization.name}
                              </span>
                            )}
                            <CategoryBadge category={ticket.category} />
                            <span>
                              {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                            </span>
                            {ticket.response_count > 0 && (
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {ticket.response_count}
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Ticket Modal */}
        <CreateTicketModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          recipients={recipients}
          categories={categories}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchTickets();
            fetchStats();
          }}
        />
      </main>
    </div>
  );
};

// Create Ticket Modal Component
const CreateTicketModal = ({ open, onClose, recipients, categories, onSuccess }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('normal');
  const [recipientId, setRecipientId] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedRecipient = recipients.find(r => r.id === parseInt(recipientId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recipientId) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('description', description);
      formData.append('category', category || 'other');
      formData.append('priority', priority);
      formData.append('recipient_id', recipientId);
      formData.append('recipient_role', selectedRecipient?.role || 'cfa_admin');
      if (attachment) {
        formData.append('attachment', attachment);
      }

      await api.post('/support/tickets/with-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Ticket créé avec succès');
      setSubject('');
      setDescription('');
      setCategory('');
      setPriority('normal');
      setRecipientId('');
      setAttachment(null);
      onSuccess();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-violet-600" />
            Créer un ticket
          </DialogTitle>
          <DialogDescription>
            Décrivez votre problème ou question
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Destinataire *
            </label>
            <select
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300"
              required
              data-testid="ticket-recipient-select"
            >
              <option value="">Sélectionner un destinataire</option>
              {recipients.map(r => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Titre *
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Résumez votre problème"
              required
              data-testid="ticket-subject-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description *
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez votre problème en détail..."
              rows={4}
              required
              data-testid="ticket-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Catégorie
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300"
                data-testid="ticket-category-select"
              >
                <option value="">Sélectionner</option>
                {categories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Priorité
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300"
                data-testid="ticket-priority-select"
              >
                <option value="low">Basse</option>
                <option value="normal">Normale</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <Paperclip className="w-4 h-4 inline mr-1" />
              Pièce jointe (optionnel)
            </label>
            <Input
              type="file"
              onChange={(e) => setAttachment(e.target.files?.[0] || null)}
              className="cursor-pointer"
              data-testid="ticket-attachment-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={submitting} data-testid="ticket-submit-btn">
              {submitting ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Ticket Detail Component
const TicketDetail = ({ ticketId, onBack, userRole }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/support/tickets/${ticketId}`);
        setTicket(response.data);
        
        // Mark ticket as read
        try {
          await api.post(`/support/tickets/${ticketId}/mark-read`);
          // Emit event to refresh badges
          window.dispatchEvent(new CustomEvent('refreshBadges'));
        } catch (err) {
          console.log('Could not mark ticket as read:', err);
        }
      } catch (error) {
        console.error('Error fetching ticket:', error);
        toast.error('Erreur lors du chargement du ticket');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketId]);

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!responseText.trim()) return;

    setSubmitting(true);
    try {
      await api.post(`/support/tickets/${ticketId}/respond`, {
        content: responseText,
        is_internal: false
      });
      
      // Refresh ticket
      const response = await api.get(`/support/tickets/${ticketId}`);
      setTicket(response.data);
      setResponseText('');
      toast.success('Réponse envoyée');
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error('Erreur lors de l\'envoi de la réponse');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setChangingStatus(true);
    try {
      await api.put(`/support/tickets/${ticketId}/status`, { status: newStatus });
      
      // Refresh ticket
      const response = await api.get(`/support/tickets/${ticketId}`);
      setTicket(response.data);
      toast.success(`Statut mis à jour: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('Erreur lors du changement de statut');
    } finally {
      setChangingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Ticket non trouvé</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={onBack} data-testid="back-to-tickets-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour aux tickets
      </Button>

      {/* Ticket Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl">{ticket.subject}</CardTitle>
              <CardDescription className="mt-1">
                Ticket #{ticket.id} • Créé le {new Date(ticket.created_at).toLocaleString('fr-FR')}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Auteur</p>
              <p className="font-medium">{ticket.creator?.name}</p>
              <p className="text-xs text-slate-400">{ticket.creator?.role}</p>
            </div>
            <div>
              <p className="text-slate-500">Destinataire</p>
              <p className="font-medium">{ticket.recipient?.name || '-'}</p>
              <p className="text-xs text-slate-400">{ticket.recipient?.role}</p>
            </div>
            <div>
              <p className="text-slate-500">Catégorie</p>
              <CategoryBadge category={ticket.category} />
            </div>
            {ticket.organization && (
              <div>
                <p className="text-slate-500">Organisation</p>
                <p className="font-medium">{ticket.organization.name}</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="border-t pt-4">
            <p className="text-sm text-slate-500 mb-2">Description</p>
            <p className="text-slate-800 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Attachment */}
          {ticket.attachment_url && (
            <div className="border-t pt-4">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${ticket.attachment_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700"
              >
                <Paperclip className="w-4 h-4" />
                Pièce jointe
              </a>
            </div>
          )}

          {/* Status Change (for recipients/admins) */}
          {ticket.can_change_status && ticket.status !== 'closed' && (
            <div className="border-t pt-4">
              <p className="text-sm text-slate-500 mb-2">Changer le statut</p>
              <div className="flex flex-wrap gap-2">
                {['open', 'in_progress', 'resolved', 'closed'].map(status => (
                  <Button
                    key={status}
                    variant={ticket.status === status ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleStatusChange(status)}
                    disabled={changingStatus || ticket.status === status}
                    data-testid={`status-btn-${status}`}
                  >
                    {STATUS_CONFIG[status]?.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-violet-600" />
            Conversation ({ticket.responses?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Responses */}
          <div className="space-y-4 mb-6">
            {(!ticket.responses || ticket.responses.length === 0) ? (
              <p className="text-center text-slate-500 py-4">Aucune réponse pour l'instant</p>
            ) : (
              ticket.responses.map(response => (
                <div
                  key={response.id}
                  className={`p-4 rounded-lg ${
                    response.user?.id === ticket.creator?.id
                      ? 'bg-violet-50 ml-4'
                      : 'bg-slate-50 mr-4'
                  }`}
                  data-testid={`response-${response.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{response.user?.name}</span>
                      <span className="text-xs text-slate-500 px-2 py-0.5 bg-white rounded-full">
                        {response.user?.role}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(response.created_at).toLocaleString('fr-FR')}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{response.content}</p>
                  {response.attachment_url && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${response.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-violet-600 mt-2"
                    >
                      <Paperclip className="w-3 h-3" />
                      Pièce jointe
                    </a>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Reply Form */}
          {ticket.can_respond && ticket.status !== 'closed' && (
            <form onSubmit={handleSubmitResponse} className="border-t pt-4">
              <Textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Écrivez votre réponse..."
                rows={3}
                className="mb-3"
                data-testid="response-textarea"
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={submitting || !responseText.trim()} data-testid="send-response-btn">
                  {submitting ? (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {ticket.status === 'closed' && (
            <p className="text-center text-slate-500 border-t pt-4">
              Ce ticket est fermé
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketSupportPage;
